import React, { useState, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  COLLAPSIBLE_TRIGGER_VARIANT_LIGHT,
  Collapsible,
  CollapsibleTrigger,
  Flex,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SPACING_16,
  SPACING_32,
  SpacingVertical,
} from 'mailup-web-components'

import Divider from 'Components/Divider'

import InputAddToGroup from 'Pages/RecipientsList/components/modals/bulkAddToGroup/InputAddToGroup'
import InputRandom from 'Pages/RecipientsList/components/modals/bulkAddToGroup/InputRandom'
import InputRemoveFromGroups from 'Pages/RecipientsList/components/modals/bulkAddToGroup/InputRemoveFromGroups'
import InputSetAsTest from 'Pages/RecipientsList/components/modals/bulkAddToGroup/InputSetAsTest'

import {
  modalBulkAddToGroupCanSubmitState,
  modalBulkAddToGroupSelectedCountState,
  modalBulkAddToGroupState,
} from 'Pages/RecipientsList/recoil/state/modals'

import {
  modalBulkAddToGroupCancelCallback,
  modalBulkAddToGroupConfirmCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalBulkAddToGroup'

const ModalBulkAddToGroup: FC = () => {
  const intl = useIntl()

  const [collapsedOptions, setCollapsedOptions] = useState(true)

  const { show } = useRecoilValue(modalBulkAddToGroupState)
  const canSubmit = useRecoilValue(modalBulkAddToGroupCanSubmitState)
  const count = useRecoilValue(modalBulkAddToGroupSelectedCountState)

  const handleCancel = useRecoilCallback(modalBulkAddToGroupCancelCallback, [])
  const handleConfirm = useRecoilCallback(
    modalBulkAddToGroupConfirmCallback,
    [],
  )

  const handleToggleCollapedOptions = (): void => {
    setCollapsedOptions((prev) => !prev)
  }

  return (
    <Modal
      show={show}
      onClose={handleCancel}
      data-mu-test="modal-bulk-add-to-group"
      closeOnOverlayClick
      showClose
    >
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.RecipientsList.ModalBulkAddToGroupTitle',
        })}
      />

      <ModalBody>
        <InputAddToGroup />

        <SpacingVertical variant={SPACING_32} />

        <Divider margin="0px" />

        <SpacingVertical variant={SPACING_16} />

        <CollapsibleTrigger
          collapsed={collapsedOptions}
          onChange={handleToggleCollapedOptions}
          variant={COLLAPSIBLE_TRIGGER_VARIANT_LIGHT}
          data-mu-test="modal-add-to-group__adv-options__trigger"
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.AdvancedOptions' })}
        </CollapsibleTrigger>

        <Collapsible collapsed={collapsedOptions}>
          <Flex flexDirection="column" gap="16px" padding="16px 0 16px 16px">
            <InputRemoveFromGroups />
            <InputRandom />
            <InputSetAsTest />
          </Flex>
          <Divider margin="0px" />
        </Collapsible>

        {collapsedOptions && <SpacingVertical variant={SPACING_16} />}
      </ModalBody>

      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          data-mu-test="cancel"
          onClick={handleCancel}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>
        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test="confirm"
          onClick={handleConfirm}
          disabled={!canSubmit}
        >
          {intl.formatMessage(
            { id: 'Load.RecipientsList.ActionBulkAddToGroup' },
            {
              count,
            },
          )}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalBulkAddToGroup
